import Loading from "@/core/components/globals/loading";
import { UserService } from "@/core/services/userService";
import { useQuery } from "@tanstack/react-query";
import { useUserStore } from "@/store/auth/store";
import Cookies from "js-cookie";
import { useNavigate, useLocation } from "react-router-dom";
import React, { useEffect, useState } from "react";
import { STALE_TIME } from "../constants/tanstack";
import { toast } from "react-toastify";
import { useTokenExpiry } from "@/core/hooks/useTokenExpiry";
import { tokenValidator } from "@/core/utils/tokenValidator";

export default function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const navigate = useNavigate();
  const location = useLocation();
  const pathname = location.pathname;
  const [isLoading, setIsLoading] = useState(true);
  const [loadingStatus, setLoadingStatus] = useState<string>("Chargement...");
  const token = Cookies.get('accessToken');

  // Zustand store hooks
  const user = useUserStore((state) => state.user);
  const setUser = useUserStore((state) => state.setUser);

  // Token expiry monitoring with auto-refresh
  useTokenExpiry({
    warningMinutes: 5,
    autoRefresh: true,
    onExpired: () => {
      toast.error('Votre session a expiré. Veuillez vous reconnecter.');
      navigate('/auth/login');
    },
  });

  const handleRedirect = () => {
    // User with valid KYC goes directly to dashboard
    setIsLoading(false);
  }

  const { isError: isKycError, error: kycError } = useQuery({
    queryKey: ['userKycStatus'],
    queryFn: () => UserService.getKycStatus(),
    staleTime: STALE_TIME.CRITICAL,
    enabled: !!user?.id && !user?.walletMasterAccount?.hasKycApproved,
  });

  useEffect(() => {
    if (isKycError && kycError) {
      // Error is handled by react-query
    }
  }, [isKycError, kycError]);

  const { data: userData, isError: isUserError, error: userError, isFetching } = useQuery({
    queryKey: ['getUserDetail'],
    queryFn: async () => {
      setLoadingStatus("Récupération de vos informations...");
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout

      try {
        const result = await UserService.getUser();
        clearTimeout(timeoutId);
        setLoadingStatus("Chargement de votre espace...");
        return result;
      } catch (error: any) {
        clearTimeout(timeoutId);
        if (error.name === 'AbortError') {
          throw new Error('Request timeout - please check your connection');
        }
        throw error;
      }
    },
    staleTime: STALE_TIME.CRITICAL,
    enabled: !!token && tokenValidator.isTokenValid(),
    retry: (failureCount: number, error: any) => {
      // Don't retry on 401 errors (authentication issues)
      if (error?.response?.status === 401) {
        return false;
      }
      // Retry network errors up to 2 times
      return failureCount < 2;
    },
  });

  useEffect(() => {
    if (userData) {
      setUser({
        ...userData,
        onboarding: {
          ...userData.onboarding,
          type: userData.type,
        },
      });
      handleRedirect();
    }
  }, [userData]);

  useEffect(() => {
    if (isUserError && userError) {
      const error: any = userError;

      // Handle different error types
      if (error?.response?.status === 401) {
        // Authentication error - redirect to login
        toast.error('Session invalide. Veuillez vous reconnecter.');
        navigate('/auth/login/', { replace: true });
      } else if (error?.message?.includes('timeout')) {
        // Timeout error - show error but don't logout
        toast.error('La connexion au serveur prend trop de temps. Veuillez réessayer.');
      } else if (!error?.response) {
        // Network error - show error but don't logout
        toast.error('Impossible de contacter le serveur. Vérifiez votre connexion internet.');
      } else {
        // Other errors - redirect to login
        toast.error('Une erreur est survenue. Veuillez vous reconnecter.');
        navigate('/auth/login/', { replace: true });
      }

      setIsLoading(false);
    }
  }, [isUserError, userError]);

  useEffect(() => {
    if (token) {
      // Validate token before proceeding
      if (!tokenValidator.isTokenValid()) {
        toast.error('Session invalide. Veuillez vous reconnecter.');
        Cookies.remove('accessToken');
        navigate('/auth/login');
        return;
      }

      if (user?.id) {
        handleRedirect();
      }
    } else {
      if (pathname.includes("/legal")) {
        setIsLoading(false)
      } else {
        navigate('/auth/login')
      }
    }
  }, [pathname, user]);

  if (isLoading || isFetching) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100vh', gap: '1.5rem' }}>
        <Loading />
        <p style={{ fontSize: '0.95rem', color: '#666', textAlign: 'center', maxWidth: '400px' }}>
          {loadingStatus}
        </p>
      </div>
    );
  }

  return <>{children}</>;
}
